import { AutocompleteOption, useAutocomplete } from '@/common/presentation/hooks/useAutocomplete';

import { useListJobSitesAutocompleteQuery } from '@/common/infrastructure/gql/generated/schema';
import { useState, useEffect, useMemo } from 'react';
import debounce from 'lodash-es/debounce';

/**
 * Type representing a job site from the GraphQL query result
 */
type JobSite = NonNullable<
  NonNullable<NonNullable<ReturnType<typeof useListJobSitesAutocompleteQuery>['data']>['jobSites']>['items']
>[number];

/**
 * Type for job site autocomplete options that enforces number type for value
 */
export interface JobSiteAutocompleteOption extends Omit<AutocompleteOption, 'value'> {
  value: number;
  latitude?: number | null;
  longitude?: number | null;
}

/**
 * Hook for implementing job site autocomplete functionality
 *
 * @param searchTerm - The current search term to filter job sites by
 * @param debounceMs - Debounce time in milliseconds for search term updates (default: 1000ms)
 * @param onJobSiteSelect - Optional callback when a job site is selected
 * @param customerId - Optional customer ID to filter job sites by
 * @returns Object containing job site options, loading state, error state, and filter function
 *
 * @example
 * ```tsx
 * const { options, loading, error, filterOptions } = useListJobSitesAutocomplete(
 *   'search term',
 *   300,
 *   (jobSite) => console.log('Selected job site:', jobSite),
 *   123 // customerId
 * );
 * ```
 */
export const useListJobSitesAutocomplete = (
  searchTerm: string = '',
  debounceMs: number = 1000,
  customerId?: number,
  onJobSiteSelect?: (jobSite: AutocompleteOption) => void
) => {
  // Debounce the search term to prevent excessive queries
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  const debouncedSetSearch = useMemo(() => debounce((value: string) => setDebouncedSearchTerm(value), debounceMs), [debounceMs]);

  useEffect(() => {
    debouncedSetSearch(searchTerm);
    return () => debouncedSetSearch.cancel();
  }, [searchTerm, debouncedSetSearch]);
  const numericCustomerId = customerId != null ? Number(customerId) : undefined;
  const hasCustomer = typeof numericCustomerId === 'number' && !Number.isNaN(numericCustomerId) && numericCustomerId > 0;

  const stringCustomerId = hasCustomer ? String(numericCustomerId) : undefined;

  const result = useListJobSitesAutocompleteQuery({
    variables: {
      skip: 0,
      search: debouncedSearchTerm,
      ...(stringCustomerId ? { customerId: stringCustomerId } : {})
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: !hasCustomer
  });

  return useAutocomplete<JobSite>({
    searchTerm,
    debounceMs,
    onSelect: onJobSiteSelect,
    query: (_search) => {
      return {
        data: { items: result.data?.jobSites?.items },
        loading: result.loading,
        error: result.error,
        refetch: result.refetch
      };
    },
    transform: (jobSite): JobSiteAutocompleteOption => ({
      // V2 schema uses jobsiteId (string), convert to number for compatibility
      value: parseInt((jobSite as NonNullable<unknown> & { jobsiteId?: string }).jobsiteId ?? '-1', 10) || -1,
      // V2 schema uses jobName instead of jobSiteName
      label: (jobSite as NonNullable<unknown> & { jobName?: string | null }).jobName ?? 'Unknown Job Site',
      // V2 schema uses location instead of address
      address: (jobSite as NonNullable<unknown> & { location?: string | null }).location,
      latitude: jobSite.latitude,
      longitude: jobSite.longitude
    })
  });
};
